import {_, classNames, computedFn, React, useLocalStore, observer, utils, moment, pluralize, Popover2} from '@api'
import {Button, Icon, MaybeElement, Menu, MenuItem, Popover, Spinner} from '@blueprintjs/core'
import {UNASSIGNED} from '@constants'
import {FilterItemDefinition, FILTER_UNASSIGNED_ID, FilterGroupState, FilterKey, OFFLINE_FILTER_VALUE_ID, ONLINE_FILTER_VALUE_ID, useFilterStore, COMPOUND_FILTER_SEPERATOR, IContextMenuItems, FilterChipContextMenu, ManageExtraTermsMenuItems, useStores} from '@design-board/components'
import {Chip} from '@material-ui/core'
import {KeyboardArrowDown} from '@material-ui/icons'
import {VipLocalStore, useVipLocalStore} from '@modules'
import {OFFLINE_ID, StoresInstance, isNumericAttribute, ui} from '@stores'
import {action, computed, toJS} from 'mobx'
import {useAsObservableSource} from 'mobx-react'
import {Fragment, useContext} from 'react'
import * as css from './FilterChip.css'

interface Props {
  className?: string
  filterKey?: FilterKey
  children?: React.ReactNode
}

type ChipValue = { id, value, label: string, exclude?: boolean }

export const FilterChipStoreContext = React.createContext<FilterChipStore>(undefined)
export const useFilterChipStore = () => useContext(FilterChipStoreContext)

export class FilterChipStore extends VipLocalStore implements IContextMenuItems {
  constructor(stores: StoresInstance, public props: { key: FilterKey }) {
    super(stores)
  }

  @computed get key() { return this.props.key}

  @action onClear = () => this.filter.clearSelection(this.key)

  @action onContextMenu = (e) => {
    this.contextMenu.onComponentContextMenu(e, this)
  }

  get contextMenuItems() {
    return <FilterChipStoreContext.Provider value={this}>
      <FilterChipContextMenu/>
    </FilterChipStoreContext.Provider>
  }

  @computed get isEmptyRange() {
    const {group: {range}} = this
    return range != null && range.start === range.end
  }

  @action clearOtherFilters = () => {
    const {key, filter} = this
    filter.filtersWithSelections.filter(f => f.group != key).forEach(f => filter.clearSelection(f.group))
  }

  label = (key: any, value: FilterItemDefinition, type, name, group: FilterGroupState) => {
    //console.log({key, value, type, name, group: toJS(group)})
    const {strings, catalog} = this
    let label
    if (key === FILTER_UNASSIGNED_ID) {
      label = strings.unassigned
    } else {
      if (isNumericAttribute(group.def)) {
        if (group.range) {
          const {range: {start, end}, def} = group,
            currency = ''
          label = `${currency}${start.toLocaleString()} - ${currency}${end.toLocaleString()}`
        }
      } else if (type === 'Boolean' || group?.def?.agg === 'exists') {
        if (key === true || key === 'true' || key === OFFLINE_FILTER_VALUE_ID) {
          // console.log(_.capitalize(catalog.booleanLabelFor(group.def, true)))
          return {
            id: key,
            label: _.capitalize(catalog.booleanLabelFor(group.def, true))
          }
        } else if (key === false || key === 'false' || key === ONLINE_FILTER_VALUE_ID) {
          return {
            id: key,
            label: _.capitalize(catalog.booleanLabelFor(group.def, false))
          }
        }

        if (!value) {
          const [prefix, id] = key.split(COMPOUND_FILTER_SEPERATOR)
          label = `${prefix} - ${_.capitalize(catalog.booleanLabelFor(group.def, id))}`
        } else {
          const prefix = value?.sectionValue ? `${value.sectionValue} - ` : ''
          label = `${prefix}${_.capitalize(catalog.booleanLabelFor(group.def, value?.id))}`
        }
      } else {
        label = key === '' ? strings.noValue : key
      }
    }
    return {
      label,
      id: key
    }
  }

  @computed get updatedDef() {
    const {catalog} = this
    return _.isEmpty(this.group?.def) ? catalog.caDefByName[this.key] : this.group.def
  }

  @computed get field() { return this.catalog._fieldByName[this.key]}

  @computed get group() { return this.filter.state.groups[this.key]}

  @computed get values(): Array<ChipValue> {
    const {group, updatedDef, label} = this,
      filters = [...Array.from(group.selected.keys()).map(k => ({key: k, exclude: false})), ...Array.from(group.excluded.keys()).map(k => ({key: k, exclude: true}))]

    return filters.map(({key, exclude}) => {
      if (updatedDef) {
        return {
          id: key, value: key, range: group.range,
          label: label(key, group.groupedByValue ? group.groupedByValue[key] : null, updatedDef.type, updatedDef.label ?? updatedDef.name, group)?.label, exclude
        }
      } else {
        const value = (exclude ? group.excluded : group.selected).get(key)
        return ({
          id: key,
          label: label(key, group.groupedByValue ? group.groupedByValue[key] : null, null, null, group)?.label,
          value: key,
          exclude,
          range: group.range
        })
      }
    }).filter(_.identity)
  }

  @computed get title() {
    const {
        strings, catalog: {labelForFieldName_currentCatalog, seasonSelectorFields},
        key, group: {range}, totalItemCount
      } = this,
      seasonField = seasonSelectorFields.find(season => season.field === key)

    return key == OFFLINE_ID ? strings.offlineItems
                             : pluralize(seasonField?.label ?? labelForFieldName_currentCatalog(key, 'singular', null, false), range ? 1 : totalItemCount)
  }

  @computed get totalItemCount() {
    const {group: {selected, excluded}} = this
    return selected.size + excluded.size
  }

  @computed get firstValueLabel() {
    // console.log(toJS(values))
    const {strings, group, firstValue, group: {range, def}} = this

    if (range) {
      const {start, end} = range,
        currency = ui.htmlForCurrency(def?.currency),
        label = `${currency}${start.toLocaleString()} - ${currency}${end.toLocaleString()}`

      return label
    }

    let firstValueLabel = firstValue?.label ?? firstValue?.id ?? firstValue

    if (utils.isEmptyString(firstValueLabel)) {
      firstValueLabel = strings.noValue
    }

    if (group?.def?.type === 'DateTime') {
      firstValueLabel = moment(parseInt(firstValueLabel)).format('MM/DD/YYYY')
    }

    return firstValueLabel
  }

  @computed get firstValue() {
    const {values} = this
    return !_.isEmpty(values) && values[0]
  }

  @computed get sortedValues() {
    const {strings, values, key, catalog: {fieldsByNameWithCustomSorts}} = this
    const field = fieldsByNameWithCustomSorts[key],
      customSortOrder = field?.customOrder,
      sortedValues: Array<ChipValue> = field ? _.sortBy(values, ({label}) => customSortOrder.has(label) ? customSortOrder.get(label) : customSortOrder.size + 1)
                                             : _.orderBy<{ id, label, value }>(values, v => {
          //console.log(v)
          const r = v.label ?? strings.emptyLabel
          return r === strings.noValue || r === strings.unassigned ? 'ZZZZZZ' : r
        })

    return sortedValues
  }
}

interface ToolbarChipProps {
  className?: string,
  chipLabel?: React.ReactNode,
  title?: string,
  value?: string,
  draggable?: boolean,
  onDelete?: React.EventHandler<any>,
  children?: MaybeElement
  onClick?: React.EventHandler<any>
  onMouseEnter?: React.EventHandler<any>
  onMouseLeave?: React.EventHandler<any>
  onDragStart?: React.DragEventHandler<any>
}

export const ToolbarChip = observer<ToolbarChipProps>(
  function ToolbarChip({className, chipLabel, title, value, children, onDelete, ...props}) {
    return (
      <div className={classNames(css.root, className)}>
        <Chip
          className={css.chip}
          classes={{label: classNames(css.chipLabel, css.labelEllipsis)}}
          data-type='Chip'
          component='div'
          {...props}
          label={chipLabel ?? <span className={css.chipLabelContents}>
          <span className={css.title}>{`${title || name}`}
            {value !== null ? `:  ` : ''}
          </span>
          <span className={css.value}>
              {value}
          </span>
         </span>}
          onDelete={onDelete}
        />
      </div>)
  })

export const ExtraTermsChip = observer(function ExtraTermsChip() {
  const {state: {extraTerms}} = useFilterStore()

  if (_.isEmpty(extraTerms) || extraTerms.some(s => !s.store)) return null

  const firstTerm = _.first(extraTerms)

  return (
    <div className={classNames(css.root)}>
      {extraTerms.length === 1
       ? <Chip
         key='single'
         className={css.chip}
         classes={{label: classNames(css.chipLabel, css.labelEllipsis)}}
         data-type='FilterChip'
         data-value={firstTerm.id}
         label={<span className={css.chipLabelContents}>
             <span className={css.value}>
              {firstTerm.store?.fullTitle}
             </span>
           </span>}
         onDelete={firstTerm.store?.toggleExcludeThisGroupCombination}/>
       : <Popover2
         interactionKind='hover'
         usePortal={false}
         key={'menu'}
         minimal
         position='bottom-left'
         hoverCloseDelay={500}
         content={
           <Menu className={css.menu}>
             <ManageExtraTermsMenuItems/>
           </Menu>}
       >
         <Button rightIcon='caret-down' minimal role='button' className={css.chipButton}
                 text={`Exclusions (${extraTerms.length})`}/>
       </Popover2>}
    </div>)
})

export const MyFilterChip = observer<Props>(
  function MyFilterChip({className, children, filterKey: key}) {
    const obsProps = useAsObservableSource({key}),
      store = useVipLocalStore((ws) => new FilterChipStore(ws, obsProps)),
      {
        strings, group, title, onClear, onContextMenu, isEmptyRange,
        totalItemCount, firstValue, firstValueLabel
      } = store

    if (isEmptyRange) return null

    // console.log({key, values, field, sortedValues})
    return (
      <FilterChipStoreContext.Provider value={store}>
        <div className={classNames(css.root, className)} data-key={key} onContextMenu={onContextMenu}>
          {children || (totalItemCount > 1 ?
           <Popover2
             interactionKind='hover'
             usePortal={false}
             key={'menu'}
             minimal
             position='bottom-left'
             content={<FilterChipMenu/>}>
             <Button
               rightIcon='caret-down'
               minimal
               role='button'
               data-exclude={group.selected.size === 0}
               className={css.chipButton}
               text={`${title} (${totalItemCount})`}/>
           </Popover2> :
           <Chip
             key='single'
             className={css.chip}
             classes={{label: classNames(css.chipLabel, css.labelEllipsis)}}
             data-type='FilterChip'
             data-group={key}
             data-excluded={firstValue?.exclude}
             data-value={firstValueLabel}
             label={<span className={css.chipLabelContents}>
             <span className={css.title}>{`${title || name}`}{firstValueLabel !== null ? `:  ` : ''}</span>
             <span className={css.value}>
              {!group.dataLoaded && _.isEmpty(firstValueLabel) ? <Fragment>{' ('}<Spinner size={12}/>{')'}</Fragment>
                                                               : firstValueLabel !== null ? firstValueLabel : strings.noValue}
                                                              </span>
           </span>}
             onDelete={onClear}/>)}
        </div>
      </FilterChipStoreContext.Provider>
    )
  })

export const SelectionFilterChip = observer(function SelectionFilterChip() {
  const {workspaces: {workspaceConfig: config}, filter, catalog: {settings: {freezeIds}}} = useStores()

  return (
    <div className={classNames(css.root)}>
      <Chip
        key='single'
        className={css.chip}
        classes={{label: css.chipLabel}}
        label={<span className={css.labelText}>{freezeIds.length === 1 ? config?.itemName : pluralize(config?.itemName)} (from Selection)</span>}
        onDelete={filter.resetFreezeIds}/>
    </div>
  )
})

export const FilterChipMenu = observer(function FilterChipMenu() {
  return (
    <Menu className={css.menu}>
      <FilterChipsMenuItems />
    </Menu>
  )
})

export const FilterChipsMenuItems = observer(function FilterChipsMenuItems() {
  const {
    group, sortedValues,
    strings, catalog: {offlineItemCount, onlineItemCount},
    filter, filter: {state: {more}}, key, onClear
  } = useFilterChipStore()

  return (<Fragment>
      {!group.dataLoaded && _.some(sortedValues, o => _.isEmpty(o))
       ? <MenuItem text='Loading...'/>
       : sortedValues.map((chip: any, i) => {
          let text = chip.label ?? chip.name ?? strings.noValue
          const count = group?.store?.groupName === OFFLINE_ID ?
                        (chip.id == 'offline' ? offlineItemCount : onlineItemCount)
                                                               : (group.groupedByValue && group.groupedByValue[chip.id]?.count) || (_.find(more[key], {name: _.isString(chip.id) ? chip.id.trim() : chip.id}) as any)?.count || 0
          if (group?.def?.type === 'DateTime') {
            text = text.toLowerCase() !== UNASSIGNED ? moment(parseInt(text)).format('MM/DD/YYYY') : text
          }
          return <MenuItem
            key={chip.id || i}
            data-exclude={chip.exclude}
            text={<ChipLabelAndCount label={text} count={count}/>}
            shouldDismissPopover={false}
            labelElement={<Icon icon='cross'/>}
            onClick={e => {
              filter.remove(key, chip.id, chip.exclude)
            }}
          />
        })}
      <MenuItem className={css.clearMenuItem}
                shouldDismissPopover={false}
                text='Clear Filter'
                onClick={onClear}/>
    </Fragment>)
})
export const ChipLabelAndCount = ({label, count}: { label: string, count: number }) => {
  return <div className={css.labelAndCount}><span className={css.label}>{label}</span>{_.isNumber(count) && <span className={css.count}>({count})</span>}</div>
}


// WEBPACK FOOTER //
// ./src/modules/catalog/FilterChipsToolbar/chips/FilterChip.tsx